import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import Ignis from './pages/Ignis';
import Sobre from './pages/Sobre';
import Pilotos from './pages/Pilotos';
import Pilots from './pages/Pilots';
import Limes from './pages/Limes';
import LimesComoFunciona from './pages/LimesComoFunciona';
import Municipios from './pages/Municipios';
import Dashboard from './pages/Dashboard';
import RiskMap from './pages/RiskMap';
import Simulation from './pages/Simulation';
import CountryRisk from './pages/CountryRisk';
// Terreno pulls in three.js (~1.2 MB); lazy-load so only /ignis/terreno pays it.
const Terreno = lazy(() => import('./pages/Terreno'));
// Limes 3D console pulls in Cesium (large); lazy-load so only /limes pays it.
const LimesMunicipio = lazy(() => import('./pages/LimesMunicipio'));
import ModelComparison from './pages/ModelComparison';
import Briefing from './pages/Briefing';
import Nowcast from './pages/Nowcast';
import Municipio from './pages/Municipio';
import Validacao from './pages/Validacao';
import RiscoIndex from './pages/RiscoIndex';
import ConcelhoRisco from './pages/ConcelhoRisco';
import DistritoHub from './pages/DistritoHub';
import Entrar from './pages/Entrar';
import Registar from './pages/Registar';
import Recuperar from './pages/Recuperar';
import Redefinir from './pages/Redefinir';
import Conta from './pages/Conta';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Topbar from './components/layout/Topbar';
import SiteFooter from './components/layout/SiteFooter';

// Full-viewport app surfaces (maps, consoles, gated tools) render without the
// site footer; every explaining page gets it (SITE_PLAN section 4).
const NO_FOOTER = [
  /^\/ignis\/(mapa-nacional|map|terreno|municipio|nowcast|simulation)(\/|$)/,
  /^\/limes\/(?!como-funciona(\/|$)).+/,
];

function FooterSwitch() {
  const { pathname } = useLocation();
  if (NO_FOOTER.some((re) => re.test(pathname))) return null;
  return <SiteFooter />;
}

function App() {
  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gradient-to-b from-tutela-warm to-white flex flex-col">
        <Topbar />
        <main className="flex-1">
          <Routes>
            {/* Marketing pages */}
            <Route path="/" element={<Landing />} />
            <Route path="/sobre" element={<Sobre />} />
            <Route path="/pilotos" element={<Pilotos />} />
            <Route path="/pilots" element={<Pilots />} />
            <Route path="/municipios" element={<Municipios />} />

            {/* LIMES public face: product page + method page (indexable). The
                functionality stays behind login at /limes/:slug below. */}
            <Route path="/limes" element={<Limes />} />
            <Route path="/limes/como-funciona" element={<LimesComoFunciona />} />

            {/* IGNIS module */}
            <Route path="/ignis" element={<Ignis />} />
            <Route path="/ignis/dashboard" element={<Dashboard />} />
            <Route path="/ignis/map" element={<RiskMap />} />
            {/* Public route; the page self-gates (explainer vs simulator) on the
                simulation grant. The backend /simulation/run is the real boundary. */}
            <Route path="/ignis/simulation" element={<Simulation />} />
            <Route path="/ignis/mapa-nacional" element={<CountryRisk />} />
            {/* Isolated 3D risk terrain (experimental, own menu entry).
                Lazy-loaded (three.js) so it never weighs down the other routes. */}
            <Route
              path="/ignis/terreno"
              element={
                <Suspense
                  fallback={
                    <div className="grid h-[calc(100vh-4rem)] place-items-center bg-ink-950">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-ember/30 border-t-ember" />
                    </div>
                  }
                >
                  <Terreno />
                </Suspense>
              }
            />
            <Route path="/ignis/briefing" element={<Briefing />} />
            <Route path="/ignis/nowcast" element={<Nowcast />} />
            <Route path="/ignis/municipio/:slug" element={<Municipio />} />
            <Route path="/ignis/municipio" element={<Municipio />} />

            {/* LIMES console (gated, customer-only, noindex, not in sitemap).
                Moves under /municipio/:slug in SITE_PLAN phase 3. */}
            {(
              [
                ['/limes/:slug/lista', 'lista'],
                ['/limes/:slug', 'mapa'],
              ] as const
            ).map(([path, view]) => (
              <Route
                key={path}
                path={path}
                element={
                  <Suspense
                    fallback={
                      <div className="grid h-[calc(100vh-4rem)] place-items-center bg-ink-950">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ember/30 border-t-ember" />
                      </div>
                    }
                  >
                    <LimesMunicipio view={view} />
                  </Suspense>
                }
              />
            ))}
            <Route path="/ignis/modelos" element={<ModelComparison />} />
            <Route path="/ignis/validacao" element={<Validacao />} />

            {/* SEO: per-concelho risk pages (278) + district hubs (18) */}
            <Route path="/ignis/risco" element={<RiscoIndex />} />
            <Route path="/ignis/risco/distrito/:slug" element={<DistritoHub />} />
            <Route path="/ignis/risco/:slug" element={<ConcelhoRisco />} />

            {/* Auth */}
            <Route path="/entrar" element={<Entrar />} />
            <Route path="/registar" element={<Registar />} />
            <Route path="/recuperar" element={<Recuperar />} />
            <Route path="/redefinir" element={<Redefinir />} />
            <Route
              path="/conta"
              element={
                <ProtectedRoute>
                  <Conta />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
        <FooterSwitch />
      </div>
    </BrowserRouter>
  );
}

export default App;
